import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import RatePlanInclusionCreateForm from '~/components/Common/Forms/RatePlanInclusionCreateForm';
import RoomRateInclusionsForm from '~/components/Common/Forms/RoomRateInclusionsForm';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import Spinner from '~/components/Common/Spinner';
import RoomRateInclusionsTable from '~/components/Vendors/Properties/RoomRateInclusionsTable';

import ReservationService from '~/services/ReservationService';
import VendorsService from '~/services/VendorsService';

type RoomRateInclusion = App.RoomRateInclusion;

interface GroupedInclusionsByRoomRate {
  room_rate_id: string;
  room_rate_name: string;
  room_type_id: string;
  property_id: string;
  inclusions: Array<RoomRateInclusion>;
}

function normalizeGroupedInclusions(
  result: Array<GroupedInclusionsByRoomRate> | Record<string, GroupedInclusionsByRoomRate> | null | undefined,
): Array<GroupedInclusionsByRoomRate> {
  if (Array.isArray(result)) {
    return result;
  }

  return Object.values(result || {});
}

export default function RatePlanInclusionsPage() {
  const { id_vendor: vendorId, id_rate_plan: ratePlanId } = useParams<{ id_vendor: string; id_rate_plan: string }>();

  const [loadingState, setLoadingState] = useState<Utils.FetchingState>('loading');
  const [vendor, setVendor] = useState<any>(null);
  const [ratePlan, setRatePlan] = useState<any>(null);
  const [groups, setGroups] = useState<Array<GroupedInclusionsByRoomRate>>([]);
  const [error, setError] = useState<string | null>(null);
  const [inclusionsSchema, setInclusionsSchema] = useState<any>(null);
  const [ratePlanInclusionsSchema, setRatePlanInclusionsSchema] = useState<any>(null);
  const [editingInclusion, setEditingInclusion] = useState<{
    inclusion: App.RoomRateInclusion;
    roomRateId: string;
  } | null>(null);
  const [creatingInclusion, setCreatingInclusion] = useState<boolean>(false);
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(new Set());
  const showConfirmDialog = useConfirmDialog();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setLoadingState('loading');
    setError(null);

    Promise.all([
      ReservationService.getRatePlan(ratePlanId),
      VendorsService.getVendorById(vendorId),
      ReservationService.getRatePlanInclusions(ratePlanId),
      ReservationService.getRatePlanInclusionsSchema(ratePlanId),
    ])
      .then(async ([ratePlanRes, vendorRes, inclusionsRes, ratePlanSchemaRes]) => {
        setVendor(vendorRes.result);
        setRatePlan(ratePlanRes.result);

        const grouped = normalizeGroupedInclusions(inclusionsRes.result);
        setGroups(grouped);

        // Set rate plan inclusions schema for create form
        setRatePlanInclusionsSchema(ratePlanSchemaRes.post?.body?.schema || ratePlanSchemaRes);

        // Fetch schema once for the first group (all groups share the same schema)
        if (grouped.length > 0) {
          const firstGroup = grouped[0];
          const schema = await ReservationService.getRoomRateInclusionsSchema(
            firstGroup.property_id,
            firstGroup.room_type_id,
            firstGroup.room_rate_id,
          );
          setInclusionsSchema(schema.post?.body?.schema);
        }

        setLoadingState('success');
      })
      .catch(() => {
        setError("Can't load rate plan inclusions");
        setLoadingState('failed');
      });
  }, [vendorId, ratePlanId]);

  const pageTitle = useMemo(() => {
    if (!ratePlan) return 'Rate Plan Inclusions';
    return `Inclusions | ${ratePlan.name}`;
  }, [ratePlan]);

  const handleAccordionChange = (roomRateId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(roomRateId);
      } else {
        newSet.delete(roomRateId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allRoomRateIds = new Set(groups.map((group) => group.room_rate_id));
    setExpandedAccordions(allRoomRateIds);
  };

  const collapseAll = () => {
    setExpandedAccordions(new Set());
  };

  const refreshInclusions = async () => {
    try {
      const inclusionsRes = await ReservationService.getRatePlanInclusions(ratePlanId);
      const grouped = normalizeGroupedInclusions(inclusionsRes.result);
      setGroups(grouped);
      setExpandedAccordions((prev) => {
        const next = new Set(grouped.map((group) => group.room_rate_id));
        return new Set(Array.from(prev).filter((roomRateId) => next.has(roomRateId)));
      });
    } catch (error) {
      console.error('Error refreshing inclusions:', error);
    }
  };

  const handleDeleteAllInclusionsForGroup = async (group: GroupedInclusionsByRoomRate) => {
    if (group.inclusions.length === 0) {
      return;
    }

    const confirmed = await showConfirmDialog({
      title: `Delete all inclusions for ${group.room_rate_name}?`,
      description: 'This will remove every inclusion in this room rate. This cannot be undone.',
    });

    if (!confirmed) {
      return;
    }

    const previousGroups = groups;

    try {
      await Promise.all(
        group.inclusions.map((inclusion) =>
          ReservationService.deleteRoomRateInclusion(
            inclusion.id,
            group.property_id,
            group.room_type_id,
            group.room_rate_id,
          ),
        ),
      );
      setGroups((prev) =>
        prev.map((currentGroup) =>
          currentGroup.room_rate_id === group.room_rate_id ? { ...currentGroup, inclusions: [] } : currentGroup,
        ),
      );
      enqueueSnackbar('All inclusions deleted successfully', { variant: 'success' });
      await refreshInclusions();
    } catch (error) {
      setGroups(previousGroups);
      enqueueSnackbar(`Error deleting inclusions: ${(error as Error).message}`, { variant: 'error' });
    }
  };

  if (loadingState === 'loading') {
    return (
      <Container>
        <Spinner />
      </Container>
    );
  }

  if (loadingState === 'failed') {
    return (
      <Container>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Helmet>
        <title>
          Vendors | {vendor?.name || vendorId} | {pageTitle}
        </title>
      </Helmet>

      <PageHeader title="Rate plan inclusions" backButton={`/vendors/${vendorId}/rate-plans/${ratePlanId}`}>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => setCreatingInclusion(true)}>
            Create
          </Button>
          <Button variant="text" onClick={expandAll} startIcon={<ExpandMoreIcon />}>
            Expand All
          </Button>
          <Button variant="text" onClick={collapseAll} startIcon={<ExpandLessIcon />}>
            Collapse All
          </Button>
        </Stack>
      </PageHeader>

      <Box mt={2}>
        {groups.length === 0 && <Typography>No inclusions for this rate plan.</Typography>}

        {groups.map((group, idx) => {
          const handleDelete = async (inclusionId: string) => {
            await ReservationService.deleteRoomRateInclusion(
              inclusionId,
              group.property_id,
              group.room_type_id,
              group.room_rate_id,
            );
            setGroups((prev) => {
              const copy = [...prev];
              const updated = { ...copy[idx], inclusions: copy[idx].inclusions.filter((i) => i.id !== inclusionId) };
              copy[idx] = updated as any;
              return copy;
            });
          };

          const handleOrderUpdate = async (orderedIds: Array<string>) => {
            await ReservationService.updateRoomRateInclusions(
              orderedIds,
              group.property_id,
              group.room_type_id,
              group.room_rate_id,
            );
          };

          const handleEdit = (inclusion: App.RoomRateInclusion) => {
            setEditingInclusion({ inclusion, roomRateId: group.room_rate_id });
          };

          return (
            <Accordion
              key={group.room_rate_id}
              disableGutters
              expanded={expandedAccordions.has(group.room_rate_id)}
              onChange={handleAccordionChange(group.room_rate_id)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{group.room_rate_name}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <RoomRateInclusionsTable
                  inclusions={group.inclusions}
                  inclusionsSchema={inclusionsSchema || { properties: { unit: { enum: [], enumNames: [] } } }}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  onInclusionsOrderUpdate={handleOrderUpdate}
                  onDeleteAll={() => handleDeleteAllInclusionsForGroup(group)}
                />
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Create Dialog */}
      {creatingInclusion && (
        <Dialog open={creatingInclusion} onClose={() => setCreatingInclusion(false)} maxWidth="md" fullWidth>
          <DialogContent>
            <RatePlanInclusionCreateForm
              ratePlanId={ratePlanId}
              groups={groups}
              schema={ratePlanInclusionsSchema}
              vendorCurrencyCode={vendor?.currency_code || 'AUD'}
              onCancel={() => setCreatingInclusion(false)}
              onInclusionCreated={() => {
                setCreatingInclusion(false);
                refreshInclusions();
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Dialog */}
      {editingInclusion && (
        <Dialog open={!!editingInclusion} onClose={() => setEditingInclusion(null)} maxWidth="md" fullWidth>
          <DialogContent>
            <RoomRateInclusionsForm
              vendorCurrencyCode={vendor?.currency_code || 'AUD'}
              inclusionItem={editingInclusion.inclusion}
              inclusionsSchema={inclusionsSchema}
              propertyId={groups.find((g) => g.room_rate_id === editingInclusion.roomRateId)?.property_id}
              roomTypeId={groups.find((g) => g.room_rate_id === editingInclusion.roomRateId)?.room_type_id}
              roomRateId={editingInclusion.roomRateId}
              onCancel={() => setEditingInclusion(null)}
              onRoomRateInclusionUpdated={() => {
                setEditingInclusion(null);
                refreshInclusions();
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
}
